import { DecoratorNode, $applyNodeReplacement } from 'lexical'
import { parseItemUrl } from '@/lib/url'

function $convertItemMentionElement (domNode) {
  const id = domNode.getAttribute('data-lexical-item-mention-id')
  const text = domNode.getAttribute('data-lexical-item-mention-text') ?? domNode.querySelector('a')?.textContent
  const url = domNode.getAttribute('data-lexical-item-mention-url') ?? domNode.querySelector('a')?.getAttribute('href')

  if (id) {
    const node = $createItemMentionNode({ id, text, url })
    return { node }
  }

  return null
}

export function isCustomText (text, id) {
  return text && text !== `#${id}`
}

export class ItemMentionNode extends DecoratorNode {
  __itemMentionId
  __text
  __url

  static getType () {
    return 'item-mention'
  }

  getItemMentionId () {
    return this.__itemMentionId
  }

  getText () {
    return this.__text
  }

  getURL () {
    return this.__url
  }

  getDisplayText () {
    if (this.__text) return this.__text
    try {
      // derive the label straight from the url's path
      const { linkText } = parseItemUrl(new URL(this.__url))
      if (linkText) return linkText
    } catch {}
    return `#${this.__itemMentionId}`
  }

  static clone (node) {
    return new ItemMentionNode(node.__itemMentionId, node.__text, node.__url, node.__key)
  }

  static importJSON (serializedNode) {
    return $createItemMentionNode({ id: serializedNode.itemMentionId, text: serializedNode.text, url: serializedNode.url })
  }

  constructor (itemMentionId, text, url, key) {
    super(key)
    this.__itemMentionId = itemMentionId
    this.__text = text
    this.__url = url
  }

  exportJSON () {
    return {
      type: 'item-mention',
      version: 1,
      itemMentionId: this.__itemMentionId,
      text: this.__text,
      url: this.__url
    }
  }

  createDOM (config) {
    const domNode = document.createElement('span')
    const theme = config.theme
    const className = isCustomText(this.__text, this.__itemMentionId) ? theme.link : theme.itemMention
    if (className !== undefined) {
      domNode.className = className
    }
    domNode.setAttribute('data-lexical-item-mention', true)
    domNode.setAttribute('data-lexical-item-mention-id', this.__itemMentionId)
    // text/url aren't derivable from id alone, so serialize them for hydration
    this.__text && domNode.setAttribute('data-lexical-item-mention-text', this.__text)
    this.__url && domNode.setAttribute('data-lexical-item-mention-url', this.__url)
    return domNode
  }

  // we need to find a way to allow display name changes
  exportDOM (editor) {
    const wrapper = document.createElement('span')
    wrapper.setAttribute('data-lexical-item-mention', true)
    const theme = editor._config.theme
    const className = isCustomText(this.__text, this.__itemMentionId) ? theme.link : theme.itemMention
    if (className !== undefined) {
      wrapper.className = className
    }
    wrapper.setAttribute('data-lexical-item-mention-id', this.__itemMentionId)
    const a = document.createElement('a')
    a.setAttribute('href', this.__url)
    a.textContent = this.getDisplayText()
    wrapper.appendChild(a)
    return { element: wrapper }
  }

  static importDOM () {
    return {
      span: (domNode) => {
        if (!domNode.hasAttribute('data-lexical-item-mention')) return null
        return { conversion: $convertItemMentionElement, priority: 1 }
      }
    }
  }

  isInline () {
    return true
  }

  updateDOM () {
    return false
  }

  getTextContent () {
    return this.getDisplayText()
  }

  decorate () {
    const ItemPopover = require('@/components/item-popover').default
    const MentionsComponent = require('@/components/editor/nodes/mentions').default
    const id = this.__itemMentionId
    const href = this.__url
    const text = this.getDisplayText()
    return (
      <ItemPopover id={id}>
        <MentionsComponent nodeKey={this.getKey()} href={href} text={text} />
      </ItemPopover>
    )
  }
}

export function $createItemMentionNode ({ id, text, url }) {
  return $applyNodeReplacement(new ItemMentionNode(id, text, url))
}

export function $isItemMentionNode (node) {
  return node instanceof ItemMentionNode
}
